import React, { createContext, useEffect, useState } from 'react'

import { isWithinDeliveryRadius, SHIPMENT_DELIVERY_DISCREPANCY_FLAGS } from '../../utils'
import { PERMISSION_STATES } from '../../../../../../../utils/permissions-service'
import PSMDeliveryAuthentication from './PSMDeliveryAuthentication'

export const AuthenticationContext = createContext()

const NUMBER_OF_DIGITS = 4
const DIRECTIONS = {
  LEFT: 'left',
  RIGHT: 'right'
}

// helper auth details
const AUTH_TYPES = {
  location: {
    title: 'Location confirmation',
    type: 'location',
    checkboxText: 'Unable to verify your location?',
    buttonText: 'Verify my location',
    alternateButtonText: 'Proceed'
  },
  otp: {
    title: 'OTP confirmation',
    type: 'otp',
    checkboxText: 'Unable to verify OTP?',
    buttonText: 'Submit',
    alternateButtonText: 'Submit'
  }
}

const DEFAULT_OTP_INPUTS = new Array(NUMBER_OF_DIGITS).fill('')

const PSMDeliveryAuthenticationContainer = ({
  api,
  otpKey,
  skipGPSValidation,
  skipOTPValidation,
  isBottomDrawerOpen,
  history,
  shipmentId,
  snapshotId,
  permission,
  geoPosition,
  position,
  distance,
  facilityName,
  locationObject,
  positionErrorMsg,
  onClose,
  trackingHandler
}) => {
  const [locationAuthType, otpAuthType] = Object.keys(AUTH_TYPES)

  const [otp, setOtp] = useState(DEFAULT_OTP_INPUTS)
  const [authType, setAuthType] = useState(skipGPSValidation && !skipOTPValidation ? otpAuthType : locationAuthType)
  const [direction, setDirection] = useState(DIRECTIONS.LEFT)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [withAlternateText, setWithAlternateText] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [authOptions, setAuthOptions] = useState({
    location: {
      isAuthenticated: false,
      isUnverified: false,
      hasError: false,
      comment: ''
    },
    otp: {
      isAuthenticated: false,
      isUnverified: false,
      hasError: false,
      comment: ''
    }
  })

  const isWithinLocationRadius = isWithinDeliveryRadius(distance)

  const authTypeInfo = AUTH_TYPES[authType]
  const { location: locationOption, otp: otpOption } = authOptions

  // VIEWS
  const isLocationAuthType = authType === locationAuthType
  const isOTPAuthType = authType === otpAuthType

  const isDeniedPermission = permission === PERMISSION_STATES.DENIED || positionErrorMsg

  const onSetTimeout = (direction = DIRECTIONS.RIGHT, nextAuthType = otpAuthType) => {
    setAuthType(nextAuthType)
    setTimeout(() => {
      setDirection(direction)
    }, 600)
  }

  const onSetAuthOptions = (prop, value) => {
    setAuthOptions(prevOptions => {
      return {
        ...prevOptions,
        [authType]: {
          ...prevOptions[authType],
          [prop]: value
        }
      }
    })
  }

  // Toggle view
  const onToggleView = (deniedGPSPermissionComment = '') => {
    setIsSaving(true)
    onSaveFailedValidationComment(deniedGPSPermissionComment)
  }

  const onHandleFailedValidationLogEvent = (fieldFlagged) => {
    trackingHandler.shipment.flagged({ shipmentId, fieldFlagged })
  }

  const onSaveGPSValidationFailedComment = async (deniedGPSPermissionComment) => {
    const gpsValidationFailedComment = isDeniedPermission ? deniedGPSPermissionComment : locationOption.comment
    await api.shipment.addGpsValidationFailComment(snapshotId, gpsValidationFailedComment, position)
    onHandleFailedValidationLogEvent(SHIPMENT_DELIVERY_DISCREPANCY_FLAGS.LOCATION_VERIFICATION)

    // Check if OTP validation should be skipped.
    // Previously, OTP was assumed to be the final step.
    // Now that this is no longer the case, we had to move some
    // of the final step logic higher up in the flow.
    if (skipOTPValidation) {
      // If OTP is skipped, navigate directly to the pick list page
      // This completes the authentication flow since there's no next step required
      history.push(`/shipments/pick-list/${snapshotId}`)
    } else {
      // If OTP is not skipped, follow the existing flow
      onSetTimeout()
      onCloseModal(true)
    }
    setIsSaving(false)
  }

  const onSaveOTPValidationFailedComment = async () => {
    await api.shipment.otp.couch.createFailSnapshot(snapshotId, otpOption.comment)
    onHandleFailedValidationLogEvent(SHIPMENT_DELIVERY_DISCREPANCY_FLAGS.OTP_VALIDATION)

    setIsSaving(false)
    history.push(`/shipments/pick-list/${snapshotId}`)
  }

  // Save GPS/OTP validation failed comment
  const onSaveFailedValidationComment = async (deniedGPSPermissionComment) => {
    try {
      if (isLocationAuthType && (locationOption.comment || deniedGPSPermissionComment)) {
        return onSaveGPSValidationFailedComment(deniedGPSPermissionComment)
      }

      if (isOTPAuthType && otpOption.comment) {
        onSaveOTPValidationFailedComment()
      }
    } catch (error) {
      console.warn(error)
    }
  }

  // VALIDATE GPS
  const onAuthenticateLocation = async () => {
    setIsVerifying(true)

    try {
      const response = await api.shipment.gpsValidation(shipmentId, {
        lat: position.lat,
        long: position.lng
      })

      if (response && response.comment) {
        onSetAuthOptions('isAuthenticated', true)

        // Check if OTP validation should be skipped.
        // Previously, OTP was assumed to be the final step.
        // Now that this is no longer the case, we had to move some
        // of the final step logic higher up in the flow.
        if (skipOTPValidation) {
          // If OTP is skipped, navigate directly to the pick list page
          // This completes the authentication flow since GPS validation succeeded
          // and there's no next authentication step required
          history.push(`/shipments/pick-list/${snapshotId}`)
        } else {
          // If OTP is not skipped, transition to the OTP authentication view
          onSetTimeout()
        }
      }
    } catch (error) {
      console.warn(error)
      onSetAuthOptions('hasError', true)
    } finally {
      setIsVerifying(false)
    }
  }

  // VALIDATE OTP
  const onAuthenticateOTP = async () => {
    setIsVerifying(true)

    const otpPassword = otp.join('')
    const otpConfig = api.shipment.otp.getConfig(otpKey.version)

    if (otpPassword.length !== otpConfig.length) {
      onSetAuthOptions('hasError', true)
      setIsVerifying(false)
      return
    }

    try {
      // verify OTP
      const verified = await api.shipment.otp.verify(
        window.crypto, otpPassword, otpKey
      )

      if (!verified) {
        onSetAuthOptions('hasError', true)
        return
      }

      // Store password in shipment snapshot
      await api.shipment.otp.couch.createPasswordSnapshot(snapshotId, otpPassword)
      onSetAuthOptions('isAuthenticated', verified)

      history.push(`/shipments/pick-list/${snapshotId}`)
    } catch (error) {
      console.warn(error)
      onSetAuthOptions('hasError', true)
    } finally {
      setIsVerifying(false)
    }
  }

  const onDeniedPermission = () => {
    const comment = 'Location permission was denied'
    onSetAuthOptions('comment', comment)
    onToggleView(comment)
  }

  const onAuthenticate = (type) => {
    if (isLocationAuthType) {
      if (locationOption.isUnverified || !isWithinLocationRadius) {
        if (isDeniedPermission) {
          return onDeniedPermission()
        }
        return onOpenModal()
      }
      onAuthenticateLocation()
    }

    if (isOTPAuthType) {
      if (otpOption.isUnverified) {
        return onOpenModal()
      }
      onAuthenticateOTP()
    }
  }

  const onOpenModal = () => {
    setIsModalOpen(true)
  }

  const onCloseModal = (preserveComment = false) => {
    // we need the `preserveComment` arg
    // to keep the comment when we toggle the view
    if (isLocationAuthType && !preserveComment) {
      onSetAuthOptions('comment', '')
    }

    if (isOTPAuthType) {
      onSetAuthOptions('comment', '')
    }
    setIsModalOpen(false)
  }

  const onHandleOTPChange = (value, index) => {
    if (otpOption.hasError) {
      onSetAuthOptions('hasError', false)
    }

    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)
  }

  const onHandleCheckboxToggle = (event) => {
    const isChecked = event.target.checked
    onSetAuthOptions('isUnverified', isChecked)

    if (isChecked) {
      if (isLocationAuthType) {
        setWithAlternateText(true)
      }

      if (isOTPAuthType) {
        setOtp(DEFAULT_OTP_INPUTS)
      }
    } else {
      setWithAlternateText(false)
    }
  }

  const onHandleCommentChange = (event) => {
    const comment = event
    onSetAuthOptions('comment', comment)
  }

  useEffect(() => {
    if ((isLocationAuthType && isDeniedPermission)) {
      onSetAuthOptions('hasError', true)
    }
  }, [isLocationAuthType, isDeniedPermission])

  const values = {
    isBottomDrawerOpen,
    history,
    permission,
    position,
    locationObject,
    positionErrorMsg,
    otp,
    authType,
    authTypeInfo,
    isLocationAuthType,
    isOTPAuthType,
    authOptions,
    direction,
    distance,
    facilityName,
    withAlternateText,
    isModalOpen,
    isDeniedPermission,
    isVerifying,
    isSaving,
    isWithinLocationRadius,
    onClose,
    onCloseModal,
    onToggleView,
    onHandleOTPChange,
    onHandleCheckboxToggle,
    onHandleCommentChange,
    onAuthenticate
  }

  return (
    <AuthenticationContext.Provider value={values}>
      <PSMDeliveryAuthentication />
    </AuthenticationContext.Provider>
  )
}

export default PSMDeliveryAuthenticationContainer
